import { Card, Col, Descriptions, Row, Space, Typography } from 'antd';
import moment from 'moment';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsBox2 } from 'react-icons/bs';
import { CiLocationOn } from 'react-icons/ci';
import { IoWarningOutline } from 'react-icons/io5';

import { getOrderStatus } from '../../../utils';
import '../Status.less';
import ReturnProductTable from './ReturnProductTable';
import './style.less';

const { Title } = Typography;

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

/** Descriptions-ийн ерөнхий тохиргоо: label дээр, value доор, зай багатай */
const descriptionProps = {
  size: 'small',
  colon: false,
  layout: 'vertical',
  column: { xs: 1, sm: 2 },
  labelStyle: { paddingBottom: 0 },
  contentStyle: { paddingBottom: 8 },
};

/** Картын толгой: дүрс + текст */
const CardTitle = ({ icon: Icon, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
    <Icon style={{ display: 'block', fontSize: '1em' }} />
    {children}
  </span>
);

/** Буцаалтын төлвийн badge — өнгө нь Status.less дэх төлвийн класснаас гарна */
const StatusBadge = ({ status, label }) => {
  if (!status) return null;

  return (
    <span className={`return-status-badge return-status-${status}`}>
      <span className="return-status-dot" />
      {label ?? status}
    </span>
  );
};

const Detail = ({ detail = {}, meta = {}, reasonEnum = {} }) => {
  /** Буцаах + солих бараанууд нэг хүснэгтэд */
  const allOrders = [...(detail?.orders ?? []), ...(detail?.changeOrders ?? [])];

  /**
   * Төлвийн нэр: захиалгын өөрийн meta-г эхэлж, байхгүй бол жагсаалтаас дамжсан
   * meta-гийн enumDictionary-г хэрэглэнэ (аль нь ч байхгүй бол эх утга).
   */
  const statusDictionary = {
    ...(meta?.orderStatus?.enumDictionary ?? {}),
    ...(detail?.meta?.orderStatus?.enumDictionary ?? detail?.meta?.orderStatus?.detail ?? {}),
  };
  const statusText = getOrderStatus(statusDictionary, detail?.orderStatus);

  const pickupAddress = detail?.pickupAddressArray ?? [];

  /** Буцаасан шалтгаан: dictionary байхгүй бол эх утгыг харуулна */
  const notesDictionary = {
    ...(reasonEnum?.notesDictionary ?? {}),
    ...(detail?.meta?.notesDictionary ?? {}),
  };
  const returnReason = detail?.notes?.length
    ? (notesDictionary[detail.notes[0]] ?? detail.notes[0])
    : null;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
      <Card
        size="middle"
        title={
          <Title level={5} style={{ margin: 0 }}>
            {val(detail?.code)}
          </Title>
        }
      >
        <Descriptions
          {...descriptionProps}
          column={{ xs: 1, sm: 4 }}
          items={[
            {
              key: 'code',
              label: 'Буцаалтын захиалгын дугаар',
              children: val(detail?.code),
            },
            {
              key: 'originOrder',
              label: 'Эх захиалгын дугаар',
              children: val(detail?.originOrder),
            },
            {
              key: 'date',
              label: 'Үүссэн огноо',
              children: val(
                detail?.orderStatusDate
                  ? moment(detail.orderStatusDate).format('YYYY-MM-DD HH:mm')
                  : null
              ),
            },
            {
              key: 'status',
              label: 'Захиалгын төлөв',
              children: <StatusBadge status={detail?.orderStatus} label={statusText} />,
            },
          ]}
        />
      </Card>

      <Card
        title={<CardTitle icon={BsBox2}>Барааны жагсаалт</CardTitle>}
        size="middle"
        styles={{ body: { padding: 0 } }}
      >
        <ReturnProductTable products={allOrders} />
      </Card>

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} xl={12}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card
              title={<CardTitle icon={CiLocationOn}>Очиж авах хаяг</CardTitle>}
              size="middle"
            >
              <Descriptions
                {...descriptionProps}
                items={[
                  { key: 'city', label: 'Хот/аймаг', children: val(pickupAddress[0]?.name) },
                  { key: 'district', label: 'Дүүрэг/сум', children: val(pickupAddress[1]?.name) },
                  { key: 'khoroo', label: 'Хороо/баг', children: val(pickupAddress[2]?.name) },
                  {
                    key: 'detail',
                    label: 'Дэлгэрэнгүй хаяг',
                    children: val(detail?.pickupDetail),
                  },
                  {
                    key: 'type',
                    label: 'Авах хэлбэр',
                    children: val(
                      meta?.pickupAddress?.enumDictionary?.[detail?.pickupAddressType] ??
                        detail?.meta?.pickupAddress?.enumDictionary?.[detail?.pickupAddressType]
                    ),
                  },
                ]}
              />
            </Card>

            <Card
              title={<CardTitle icon={AiOutlineInfoCircle}>Харилцагчийн мэдээлэл</CardTitle>}
              size="middle"
            >
              <Descriptions
                {...descriptionProps}
                items={[
                  { key: 'name', label: 'Нэр', children: val(detail?.customerName) },
                  { key: 'phone', label: 'Утас', children: val(detail?.customerPhone) },
                  { key: 'email', label: 'И-мэйл', children: val(detail?.customerEmail) },
                ]}
              />
            </Card>
          </Space>
        </Col>

        <Col xs={24} xl={12}>
          <Card
            title={<CardTitle icon={IoWarningOutline}>Буцаасан шалтгаан</CardTitle>}
            size="middle"
          >
            <Descriptions
              {...descriptionProps}
              column={1}
              items={[
                { key: 'reason', label: 'Шалтгаан', children: val(returnReason) },
                {
                  key: 'description',
                  label: 'Дэлгэрэнгүй',
                  children: val(detail?.orderDescription),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

export default Detail;
